import { gql } from "@apollo/client";
import client from "../apolloClient";
import {
  Permission,
  PermissionCreateFormData,
  PermissionUpdateFormData,
} from "../../schemas/permissionSchema";

export interface PermissionsResponse {
  data: Permission[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface PermissionFilters {
  page?: number;
  pageSize?: number;
  search?: string;
}

export interface UpdatePermissionPayload {
  id: string;
  permission: PermissionUpdateFormData;
}

const GET_ALL_PERMISSIONS = gql`
  query Permissions($page: Int, $pageSize: Int, $search: String) {
    permissions(page: $page, pageSize: $pageSize, search: $search) {
      permissions {
        id
        action
      }
      page
      pageSize
      total
      totalPages
      hasNextPage
      hasPrevPage
    }
  }
`;

const GET_EVERY_PERMISSION = gql`
  query AllPermissions {
    allPermissions {
      id
      action
    }
  }
`;

const CREATE_PERMISSION = gql`
  mutation CreatePermission($action: String!) {
    createPermission(action: $action)
  }
`;

const DELETE_PERMISSIONS = gql`
  mutation DeletePermissions($ids: [ID!]!) {
    deletePermissions(ids: $ids)
  }
`;

export const permissionService = {
  /**
   * Every permission, unpaginated — for the role form's matrix, which needs
   * the complete set. The paginated `getAll` below caps out at 100 server-side.
   */
  getEvery: async (): Promise<Permission[]> => {
    const { data } = await client.query({
      query: GET_EVERY_PERMISSION,
      fetchPolicy: "network-only",
    });
    return data.allPermissions;
  },

  getAll: async (
    filters: PermissionFilters = {},
  ): Promise<PermissionsResponse> => {
    const { data } = await client.query({
      query: GET_ALL_PERMISSIONS,
      variables: filters,
      fetchPolicy: "network-only",
    });
    const res = data.permissions;
    return {
      data: res.permissions || [],
      page: res.page,
      pageSize: res.pageSize,
      total: res.total,
      totalPages: res.totalPages,
      hasNextPage: res.hasNextPage,
      hasPrevPage: res.hasPrevPage,
    };
  },

  create: async (input: PermissionCreateFormData): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: CREATE_PERMISSION,
      variables: { action: input.action },
    });
    return data.createPermission;
  },

  remove: async (ids: string[]): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: DELETE_PERMISSIONS,
      variables: { ids },
    });
    return data.deletePermissions;
  },
};
